import {
  assertDockPanelDefinition,
} from "./DockPanelDefinition";
import {
  DockPanelRegistryError,
} from "./errors";


export function createDockPanelRegistry({
  panels = [],
} = {}) {
  if (!Array.isArray(panels)) {
    throw new DockPanelRegistryError(
      "DockPanelRegistry.panels must be an array.",
      "INVALID_DOCK_PANEL_COLLECTION",
    );
  }

  const entries = new Map();

  function register(panel) {
    const validatedPanel = assertDockPanelDefinition(panel);

    if (entries.has(validatedPanel.id)) {
      throw new DockPanelRegistryError(
        `Dock panel already registered: ${validatedPanel.id}.`,
        "DOCK_PANEL_ALREADY_REGISTERED",
      );
    }

    entries.set(
      validatedPanel.id,
      validatedPanel,
    );

    return validatedPanel;
  }

  function unregister(panelId) {
    const existing = get(panelId);

    if (!existing) {
      return null;
    }

    entries.delete(panelId);

    return existing;
  }

  function get(panelId) {
    if (typeof panelId !== "string") {
      return null;
    }

    return entries.get(panelId.trim()) || null;
  }

  function has(panelId) {
    return get(panelId) !== null;
  }

  function list() {
    return Object.freeze(
      Array.from(entries.values()),
    );
  }

  function clear() {
    const previous = list();
    entries.clear();

    return previous;
  }

  for (const panel of panels) {
    register(panel);
  }

  return Object.freeze({
    register,
    unregister,
    get,
    has,
    list,
    clear,
  });
}


export function isDockPanelRegistry(value) {
  return Boolean(
    value
    && typeof value === "object"
    && typeof value.register === "function"
    && typeof value.unregister === "function"
    && typeof value.get === "function"
    && typeof value.has === "function"
    && typeof value.list === "function"
    && typeof value.clear === "function"
  );
}


export function assertDockPanelRegistry(value) {
  if (!isDockPanelRegistry(value)) {
    throw new DockPanelRegistryError(
      "Expected a valid DockPanelRegistry.",
      "INVALID_DOCK_PANEL_REGISTRY",
    );
  }

  return value;
}
